import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  validateActivityPayload,
  CrosswordPreviewGrid,
  layoutCrosswordFromWords,
  parseWordDump,
  type CoopPayload,
  type CrosswordWordEntry,
} from '@interactive-room';
import { emptyCoopPayload } from '../emptyPayloads';
import { StudioLiveShell } from '../components/StudioLiveShell';
import { createDraftId, saveActivityDraft, type ActivityDraft } from '../activityStorage';
import { resolveStudioLaunchDefaults } from '../studioLaunch';
import { generateSubjectAwareActivityDraft } from '../studioActivityGeneration';
import type { StudioLiveEditorProps } from '../types/editor.types';

function cluesToEntries(payload: CoopPayload): CrosswordWordEntry[] {
  if (payload.board.kind !== 'crossword') return [];
  return payload.board.clues.map((c) => ({ word: c.answer, clue: c.clue }));
}

export function CoopLiveEditor({
  draftId,
  embedded,
  onSaved,
  launchSubjectId,
  launchPresetId,
}: StudioLiveEditorProps) {
  const launch = resolveStudioLaunchDefaults(emptyCoopPayload, {
    draftId,
    launchSubjectId,
    launchPresetId,
  });
  const [payload, setPayload] = useState<CoopPayload>(() => launch.payload);
  const [subjectTag, setSubjectTag] = useState(() => launch.subjectTag);
  const [wordEntries, setWordEntries] = useState<CrosswordWordEntry[]>(() => cluesToEntries(launch.payload));
  const [dumpText, setDumpText] = useState('');
  const [layoutAttempt, setLayoutAttempt] = useState(0);
  const [layoutError, setLayoutError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [savedId] = useState(draftId ?? createDraftId());

  const board = payload.board;
  const splitLayout = embedded && board.kind === 'crossword';

  const applyLayout = useCallback(
    (entries: CrosswordWordEntry[], attempt: number) => {
      if (entries.length === 0) return;
      const result = layoutCrosswordFromWords(entries, attempt);
      if (!result) {
        setLayoutError('No se pudo cruzar todas las palabras. Pulsa 🔄 Regenerar o reduce la lista.');
        return;
      }
      setLayoutError(null);
      setPayload((p) => ({
        ...p,
        board: result.board,
      }));
    },
    [],
  );

  useEffect(() => {
    if (board.kind !== 'crossword' || wordEntries.length === 0) return;
    applyLayout(wordEntries, layoutAttempt);
  }, [wordEntries, layoutAttempt, board.kind, applyLayout]);

  const updateEntry = (index: number, patch: Partial<CrosswordWordEntry>) => {
    setWordEntries((prev) => prev.map((e, i) => (i === index ? { ...e, ...patch } : e)));
  };

  const addEntry = () => {
    setWordEntries((prev) => [...prev, { word: '', clue: '' }]);
  };

  const removeEntry = (index: number) => {
    setWordEntries((prev) => prev.filter((_, i) => i !== index));
  };

  const importDump = () => {
    const parsed = parseWordDump(dumpText);
    if (parsed.length) setWordEntries(parsed);
  };

  const regenerate = () => setLayoutAttempt((n) => n + 1);

  const handleAiBuild = async (prompt: string) => {
    setAiLoading(true);
    setError(null);
    setNotice(null);
    try {
      const { draft, notice: aiNotice } = await generateSubjectAwareActivityDraft({
        mode: 'coop',
        subjectId: launchSubjectId,
        subjectTag: subjectTag.trim() || undefined,
        tuning: prompt,
        draftId: savedId,
        persist: false,
      });
      setPayload(draft.payload as CoopPayload);
      if (draft.subjectTag) setSubjectTag(draft.subjectTag);
      setWordEntries(cluesToEntries(draft.payload as CoopPayload));
      setLayoutAttempt(0);
      if (aiNotice) setNotice(aiNotice);
    } catch (e) {
      setError(
        e instanceof Error && e.message === 'subject_context_required'
          ? 'Selecciona una asignatura desde el Studio para generar con IA.'
          : e instanceof Error
            ? e.message
            : 'No se pudo generar con IA',
      );
    } finally {
      setAiLoading(false);
    }
  };

  const save = () => {
    try {
      validateActivityPayload('coop', payload);
      setError(null);
      onSaved?.(
        saveActivityDraft({
          id: savedId,
          mode: 'coop',
          title: payload.title,
          subjectTag: subjectTag.trim() || 'general',
          payload,
        }),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error');
    }
  };

  const wordPanel = useMemo(
    () =>
      board.kind === 'crossword' ? (
        <div className="crossword-word-dump space-y-3">
          <div>
            <p className="studio-zone__label">Volcado de palabras</p>
            <p className="studio-zone__hint">Palabra → pista. El motor cruza y dibuja la grilla solo.</p>
          </div>

          <textarea
            className="canvas-inline-input min-h-[4rem] w-full resize-y text-xs"
            value={dumpText}
            onChange={(e) => setDumpText(e.target.value)}
            placeholder={'CASA → Hogar de aprendizaje\nCOLE → Lugar donde estudiamos'}
          />
          <button type="button" className="studio-rail-btn w-full text-[10px]" onClick={importDump}>
            Importar lista
          </button>

          <div className="crossword-word-dump__list max-h-[40vh] space-y-2 overflow-y-auto">
            {wordEntries.map((entry, i) => (
              <div key={i} className="crossword-word-dump__row grid grid-cols-[1fr_1.2fr_auto] gap-1">
                <input
                  className="canvas-inline-input font-mono text-xs uppercase"
                  value={entry.word}
                  placeholder="PALABRA"
                  onChange={(e) =>
                    updateEntry(i, {
                      word: e.target.value.toUpperCase().replace(/[^A-ZÁÉÍÓÚÑ]/g, ''),
                    })
                  }
                />
                <input
                  className="canvas-inline-input text-xs"
                  value={entry.clue}
                  placeholder="Pista"
                  onChange={(e) => updateEntry(i, { clue: e.target.value })}
                />
                <button
                  type="button"
                  className="text-slate-400 hover:text-red-400"
                  onClick={() => removeEntry(i)}
                  aria-label="Eliminar"
                >
                  ×
                </button>
              </div>
            ))}
          </div>

          <div className="flex gap-2">
            <button type="button" className="studio-rail-btn flex-1 text-[10px]" onClick={addEntry}>
              + Palabra
            </button>
            <button type="button" className="studio-rail-btn flex-1 text-[10px]" onClick={regenerate} title="Regenerar diseño">
              🔄 Regenerar
            </button>
          </div>

          {layoutError && <p className="text-[10px] text-amber-600">{layoutError}</p>}
        </div>
      ) : null,
    [board.kind, dumpText, importDump, layoutError, wordEntries],
  );

  return (
    <StudioLiveShell
      embedded={embedded}
      layout={splitLayout ? 'split' : 'default'}
      error={error}
      subjectTag={subjectTag}
      onSubjectTagChange={setSubjectTag}
      onSave={save}
      sidebar={wordPanel ?? undefined}
      sidebarTitle="Palabras"
      aiActivityLabel="crucigrama"
      onAiBuild={board.kind === 'crossword' ? handleAiBuild : undefined}
      aiLoading={aiLoading}
    >
      {notice && (
        <p className="mx-4 mt-2 rounded-lg border border-amber-500/30 bg-amber-950/20 px-3 py-2 text-[10px] text-amber-200/90">
          {notice}
        </p>
      )}
      {aiLoading && (
        <p className="mx-4 mt-2 text-center text-xs text-[var(--plugin-muted)]">Generando crucigrama…</p>
      )}
      <header className="plugin-live-toolbar">
        <input
          className="canvas-inline-input canvas-inline-input--title min-w-0 flex-1 bg-transparent font-display text-base font-bold"
          value={payload.title}
          onChange={(e) => setPayload((p) => ({ ...p, title: e.target.value }))}
          placeholder="Título"
        />
        <select
          className="explora-toolbar-btn text-[10px]"
          value={board.kind}
          onChange={(e) => {
            const kind = e.target.value;
            if (kind === 'hangman') {
              setPayload((p) => ({
                ...p,
                board: { kind: 'hangman', word: 'EDUCACION', hint: '', maxErrors: 6 },
              }));
            } else {
              setPayload((p) => ({ ...p, board: emptyCoopPayload().board }));
              setWordEntries([]);
            }
          }}
        >
          <option value="crossword">Crucigrama</option>
          <option value="hangman">Ahorcado</option>
        </select>
      </header>

      <div className="plugin-live-body coop-editor-canvas flex min-h-0 flex-1 items-center justify-center overflow-auto p-3">
        {board.kind === 'crossword' ? (
          wordEntries.length === 0 ? (
            <div className="sala-empty-state max-w-sm">
              <p className="sala-empty-state__title">Crucigrama AI-first</p>
              <p className="sala-empty-state__hint">
                Escribe palabras y pistas en el panel →. La grilla se genera sola.
              </p>
            </div>
          ) : (
            <CrosswordPreviewGrid board={board} showSolution size="fill" />
          )
        ) : board.kind === 'hangman' ? (
          <div className="hangman-editor-preview text-center">
            <div className="hangman-editor-gallows mx-auto mb-6 opacity-20" aria-hidden />
            <p className="font-display text-3xl font-bold tracking-[0.35em] text-[var(--plugin-text)]">
              {board.word.split('').map(() => '_').join(' ')}
            </p>
            <p className="mt-4 text-sm opacity-50">{board.hint || 'Añade una pista'}</p>
          </div>
        ) : null}
      </div>
    </StudioLiveShell>
  );
}
